import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { checkDevelopmentDataExists } from '../api';

interface DevelopmentDataContextType {
  hasData: boolean;
  isLoading: boolean;
  checkDataExists: () => Promise<void>;
}

const DevelopmentDataContext = createContext<DevelopmentDataContextType | undefined>(undefined);

export const useDevelopmentData = (): DevelopmentDataContextType => {
  const context = useContext(DevelopmentDataContext);
  if (!context) {
    throw new Error('useDevelopmentData must be used within a DevelopmentDataProvider');
  }
  return context;
};

interface DevelopmentDataProviderProps {
  children: ReactNode;
}

export const DevelopmentDataProvider: React.FC<DevelopmentDataProviderProps> = ({ children }) => {
  const [hasData, setHasData] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const checkDataExists = async () => {
    try {
      setIsLoading(true);
      const result = await checkDevelopmentDataExists();
      setHasData(result.hasData);
    } catch (error) {
      console.error('Error checking development data:', error);
      setHasData(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkDataExists();
  }, []);

  const value = {
    hasData,
    isLoading,
    checkDataExists
  };

  return (
    <DevelopmentDataContext.Provider value={value}>
      {children}
    </DevelopmentDataContext.Provider>
  );
};
